<template>
  <div class="mt-3">
    <el-upload
      v-can-enable="'reactivation:write'"
      ref="upload"
      action=""
      :before-upload="beforeUpload"
      :on-change="handleFileChange"
      :auto-upload="false"
      :show-file-list="false"
      :disabled="processing"
      accept=".csv"
      v-show="!file"
    >
      <el-button slot="trigger">Select CSV File</el-button>
    </el-upload>

    <el-input v-if="file && progress !== 100" v-model="file.name" :disabled="true" size="small" style="width: 300px" />

    <div v-if="file && progress === 100" class="flex space-x-2">
      <div>
        <el-tag type="success">Emails successfully added to unsubscribe list</el-tag>
      </div>
      <el-button size="small" type="text" @click="clearFileList" v-if="file && !processing">Add More</el-button>
    </div>
    <div v-else class="flex space-x-3 mt-3">
      <el-button size="small" type="primary" @click="processFile" v-if="file && !processing"> Add Emails </el-button>
      <el-button size="small" type="text" @click="clearFileList" v-if="file && !processing">Cancel </el-button>
      <el-button size="small" type="text" @click="abortProcessing" v-if="processing"> Cancel </el-button>
    </div>
    <div class="mt-3" style="width: 300px">
      <el-progress :percentage="progress" status="success" v-show="progress === 100"> </el-progress>
      <el-progress :percentage="progress" v-show="progress > 0 && progress < 100"> </el-progress>
    </div>
  </div>
</template>

<script>
import Papa from 'papaparse';
import unsubscribeService from '@/api/unsubscribe.service';

const CHUNK_SIZE = 5000; // Adjust based on your testing for optimal performance

export default {
  components: {},
  data() {
    return {
      file: null,
      progress: 0,
      processing: false,
      parser: null, // Keep track of the Papa Parse instance for aborting
      count: 0,
      rowsProcessed: 0,
      totalRows: 0,
    };
  },
  methods: {
    beforeUpload(file) {
      if (this.processing) {
        this.$message.error('Please wait until the current process is finished.');
        return false;
      }
      this.clearFileList(); // Clear the file list each time a new file is about to be uploaded
      this.file = file;
      return false; // Prevent auto-upload
    },
    clearFileList() {
      if (this.$refs.upload) {
        this.$refs.upload.clearFiles(); // This is the recommended way to clear the file list
      }
      this.file = null;
      this.progress = 0;
      this.totalRows = 0;
      this.rowsProcessed = 0;
    },
    handleFileChange(file) {
      if (this.file !== file.raw) {
        this.file = file.raw || file;
        this.progress = 0; // Reset progress on new file selection
        this.totalRows = 0;
        this.rowsProcessed = 0;
      }
    },
    processFile() {
      if (!this.file) {
        this.$message.error('Please select a CSV file first.');
        return;
      }
      this.processing = true;

      const rowCount = 0; // Initialize total rows count

      Papa.parse(this.file, {
        skipEmptyLines: true, // Skip empty lines to count rows more efficiently
        step: () => {
          this.totalRows += 1; // Increment total rows for each non-empty row parsed directly in the component's data
        },
        complete: () => {
          this.parseAndProcessCSVInChunks(this.file); // Continue with processing after total rows are counted
        },
      });
    },
    parseAndProcessCSVInChunks(file) {
      this.parser = Papa.parse(file, {
        header: false,
        chunk: (results, parser) => {
          parser.pause(); // Pause parsing to process the current chunk
          this.processChunk(results.data)
            .then(() => {
              if (this.processing) parser.resume();
            })
            .catch((error) => {
              this.$message.error(`Error processing chunk: ${error.message}`);
              console.error(error);
              parser.abort();
              this.processing = false;
            });
        },
        complete: () => {
          this.progress = 100;
          this.processing = false;
          this.$message.success('All emails processed and unsubscribed successfully.');
        },
        error: (error) => {
          this.progress = 0;
          this.processing = false;
          this.$message.error(`Error parsing CSV file: ${error.message}`);
          console.error(error);
        },
        chunkSize: CHUNK_SIZE,
      });
    },
    async processChunk(rows) {
      this.rowsProcessed += rows.length;
      const { emails } = this.extractEmails(rows);
      if (emails.length === 0) {
        return Promise.resolve(); // Skip processing if no emails found
      }

      this.progress = Math.round(Math.min(100, (this.rowsProcessed / this.totalRows) * 100));
      return unsubscribeService.bulkUnsubscribe(emails);
    },
    extractEmails(rows) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const emails = [];

      rows.forEach((row) => {
        row.forEach((cell) => {
          if (emailRegex.test(cell)) {
            emails.push(cell);
          }
        });
      });

      return { emails };
    },
    abortProcessing() {
      if (this.parser) {
        this.parser.abort();
      }
      this.processing = false;
      this.progress = 0;
      this.$message.info('Processing aborted by the user.');
    },
  },
};
</script>
